import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppInsightsService } from './app-insights.service';

const SESSION_KEY = 'dwo_session_start';
const SESSION_DURATION_MS = 13 * 60 * 60 * 1000; // 13 hours
const WARN_BEFORE_MS = 30 * 60 * 1000; // warn 30 min before expiry
const EXTEND_DURATION_MS = 4 * 60 * 60 * 1000; // extend by 4 hours

@Injectable({ providedIn: 'root' })
export class AuthSessionService implements OnDestroy {
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private warnTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly msalService: MsalService,
    private readonly snackBar: MatSnackBar,
    private readonly ngZone: NgZone,
    private readonly appInsightsService: AppInsightsService,
  ) {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  startSession(): void {
    if (!sessionStorage.getItem(SESSION_KEY)) {
      sessionStorage.setItem(SESSION_KEY, Date.now().toString());
    }
    this.scheduleTimers();
  }

  private scheduleTimers(): void {
    this.clearTimers();

    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return;

    const start = parseInt(raw, 10);
    const elapsed = Date.now() - start;
    const remaining = SESSION_DURATION_MS - elapsed;

    if (remaining <= 0) {
      this.forceLogout();
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      const warnAt = remaining - WARN_BEFORE_MS;
      if (warnAt > 0) {
        this.warnTimer = setTimeout(() => {
          this.ngZone.run(() => this.showWarning());
        }, warnAt);
      }

      // Hard logout at expiry
      this.logoutTimer = setTimeout(() => {
        this.ngZone.run(() => this.forceLogout());
      }, remaining);
    });
  }

  extendSession(): void {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return;
    const newStart = parseInt(raw, 10) + EXTEND_DURATION_MS;
    sessionStorage.setItem(SESSION_KEY, newStart.toString());
    this.snackBar.open('Session extended by 4 hours.', 'OK', {
      duration: 5_000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
    this.scheduleTimers();
  }

  private showWarning(): void {
    const ref = this.snackBar.open(
      'Your session will expire in 30 minutes. Tap "Extend Session" to stay logged in.',
      'Extend Session',
      {
        duration: 0, // stays visible for the full 30-min warning window
        panelClass: 'session-warn-snackbar',
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      },
    );

    // If the user taps the action button, extend the session by 4 hours
    ref.onAction().subscribe(() => {
      this.ngZone.run(() => this.extendSession());
    });
  }

  private forceLogout(): void {
    this.clearTimers();
    this.snackBar.dismiss();
    this.appInsightsService.trackEvent('Logout', { Reason: 'expired' });
    this.appInsightsService.clearUser();
    sessionStorage.clear();
    this.msalService.logoutRedirect({
      onRedirectNavigate: () => false,
    });
  }

  /** Re-evaluates timers whenever the app tab becomes visible (handles device sleep drift). */
  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') return;

    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return;

    const elapsed = Date.now() - parseInt(raw, 10);
    if (elapsed >= SESSION_DURATION_MS) {
      this.forceLogout();
    } else {
      this.scheduleTimers();
    }
  };

  private clearTimers(): void {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
    if (this.warnTimer) {
      clearTimeout(this.warnTimer);
      this.warnTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}
